/**
 * Link Helper Utilities
 *
 * Utility functions for detecting app domain URLs and extracting paths
 * for in-app navigation on mobile.
 *
 * @example
 * extractAppPath("https://app.primum.co/content-library/123") // "/content-library/123"
 * extractAppPath("https://app.primum.co/inbox") // "/inbox"
 * extractAppPath("primum://community/456") // "/community/456"
 * extractAppPath("https://google.com") // null
 */

import { DEEP_LINK_SCHEME } from '@/constants/links';
import { env } from '@/util/env';
import { Sentry } from '@/util/sentry';

const APP_DOMAINS = ['app.primum.co', env.EXPO_PUBLIC_APP_WEB_DOMAIN || 'dev.app.primum.co'].filter(
  Boolean
);

/**
 * Extracts the app path from a URL if it's on our app domain or uses our deep link scheme.
 *
 * Supports multiple URL formats:
 * - Full URL: https://app.primum.co/content-library/123 → /content-library/123
 * - Deep link: primum://inbox/chat/456 → /inbox/chat/456
 *
 * Note: Relative paths (starting with /) are already internal routes and don't
 * need extraction - they can be used directly with router.push().
 *
 * @param url - URL to check (full URL or deep link scheme)
 * @returns The pathname with query params and fragment for internal navigation, or null if not an app domain URL
 *
 * @example
 * extractAppPath("https://app.primum.co/content-library/123") // "/content-library/123"
 * extractAppPath("https://app.primum.co/inbox?ref=banner") // "/inbox?ref=banner"
 * extractAppPath("https://dev.app.primum.co/inbox") // "/inbox"
 * extractAppPath("primum://community/456") // "/community/456"
 * extractAppPath("https://google.com/page") // null
 * extractAppPath("/profile") // null (already a relative path)
 */
export function extractAppPath(url: string): string | null {
  if (!url) {
    return null;
  }

  if (url.startsWith('/')) {
    return null;
  }

  if (url.startsWith(DEEP_LINK_SCHEME)) {
    const path = url.slice(DEEP_LINK_SCHEME.length);

    return path.startsWith('/') ? path : `/${path}`;
  }

  try {
    const parsedUrl = new URL(url);

    const isAppDomain = APP_DOMAINS.some(
      (domain) => parsedUrl.hostname === domain || parsedUrl.hostname.endsWith(`.${domain}`)
    );

    if (isAppDomain && parsedUrl.pathname) {
      return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    }
  } catch {
    /*
     * Reports the scheme only. The URL is one a person followed from an email, so it can
     * carry a sign-in token, and some URL parsers echo the input in their error message —
     * which is why the error text is not attached either.
     */
    Sentry.captureMessage('Invalid URL format in extractAppPath', {
      level: 'warning',
      extra: { scheme: /^[a-z][a-z0-9+.-]*(?=:)/i.exec(url)?.[0] ?? 'none' },
    });
  }

  return null;
}

/**
 * Checks if a URL is on our app domain or uses our deep link scheme.
 *
 * @param url - URL to check
 * @returns true if the URL should be handled as internal navigation
 *
 * @example
 * isAppDomainUrl("https://app.primum.co/inbox") // true
 * isAppDomainUrl("primum://content-library/123") // true
 * isAppDomainUrl("https://google.com") // false
 * isAppDomainUrl("/profile") // false (relative paths handled separately)
 */
export function isAppDomainUrl(url: string): boolean {
  return extractAppPath(url) !== null;
}
